/**
 * Shared planning utilities for mapping template weeks ↔ project weeks.
 *
 * Templates use a fixed REFERENCE_START date (Monday of ISO week 1, 2026).
 * Projects use a user-chosen start date. Both grids have blocked weeks
 * (congés) at different positions. This module maps between the two
 * through "working slot" space — the sequence of non-blocked weeks.
 */

import { MODEL_WEEK_COUNTS } from "./planning_constants";

/** Fixed start date for all planning templates: Monday of ISO week 1 of 2026 */
export const REFERENCE_START = new Date(2025, 11, 29); // 2025-12-29

const MODELS_WITHOUT_CONGES = new Set(["12_mois_ordre"]);

/** ISO week number (1-53) for a given date */
export function getISOWeek(d: Date): number {
  const tmp = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  tmp.setUTCDate(tmp.getUTCDate() + 4 - (tmp.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  return Math.ceil(((tmp.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

/**
 * Compute the set of blocked (congés) week indices for a given start date and model.
 * Blocked = ISO weeks 31-35 (August) + last 2 weeks of December (date >= 20).
 */
export function getBlockedIndices(startDate: Date, model: string): Set<number> {
  const blocked = new Set<number>();
  if (MODELS_WITHOUT_CONGES.has(model)) return blocked;
  const n = MODEL_WEEK_COUNTS[model] ?? 48;
  for (let i = 0; i < n; i++) {
    const monday = new Date(startDate);
    monday.setDate(monday.getDate() + i * 7);
    const isoWeek = getISOWeek(monday);
    const calMonth = monday.getMonth() + 1;
    if (
      (isoWeek >= 31 && isoWeek <= 35) ||
      (calMonth === 12 && monday.getDate() >= 20)
    ) {
      blocked.add(i);
    }
  }
  return blocked;
}

/**
 * Convert a raw column index to its "working slot" number
 * (its position among non-blocked columns, 0-based).
 *
 * E.g. if columns 5,6 are blocked: rawToSlot(7, {5,6}) = 5
 */
function rawToSlot(rawIndex: number, blocked: Set<number>): number {
  let slot = 0;
  for (let i = 0; i < rawIndex; i++) {
    if (!blocked.has(i)) slot++;
  }
  return slot;
}

/**
 * Convert a "working slot" number back to a raw column index,
 * skipping over blocked columns.
 *
 * Returns -1 if the slot exceeds available working weeks.
 */
function slotToRaw(slot: number, blocked: Set<number>, totalCols: number): number {
  let count = 0;
  for (let i = 0; i < totalCols; i++) {
    if (!blocked.has(i)) {
      if (count === slot) return i;
      count++;
    }
  }
  return -1; // slot exceeds grid
}

/**
 * Map template default_weeks (raw indices relative to REFERENCE_START)
 * to project column indices (raw indices relative to project start date).
 *
 * The mapping goes: template raw index → working slot → project raw index.
 * This ensures the Nth working week in the template maps to the Nth working
 * week in the project, with holidays creating the right gaps.
 */
export function mapTemplateWeeksToProject(
  templateWeeks: number[],
  model: string,
  projectStartDate: Date,
): number[] {
  const totalCols = MODEL_WEEK_COUNTS[model] ?? 48;
  const templateBlocked = getBlockedIndices(REFERENCE_START, model);
  const projectBlocked = getBlockedIndices(projectStartDate, model);

  return templateWeeks
    .filter((raw) => !templateBlocked.has(raw)) // skip weeks that land on template holidays
    .map((raw) => {
      const slot = rawToSlot(raw, templateBlocked);
      return slotToRaw(slot, projectBlocked, totalCols);
    })
    .filter((idx) => idx !== -1); // discard if overflows project grid
}
